import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import './Rightnav.css';

// How far below the top of the screen a heading must pass before its item
// becomes active. Roughly the sticky navbar height plus a little breathing room.
const ACTIVE_OFFSET = 120;

// After a click we scroll smoothly to the section. While that scroll runs,
// the spy is paused so the highlight doesn't flick through every item in between.
const CLICK_LOCK_MS = 800;

// Event pages can listen to (e.g. to open a collapsed SDK card before it's scrolled to).
export const DOCS_NAVIGATE_EVENT = 'docs:navigate';

const getTargets = (contents) =>
  contents
    .map((id) => ({ id, el: document.getElementById(id) }))
    .filter((t) => t.el);

// The item to highlight: the last section whose top has scrolled past the
// offset line. At the very bottom of the page, the last section wins even if
// it's too short to ever reach the line.
const findActiveId = (contents) => {
    const targets = getTargets(contents);
    if (!targets.length) return null;

    const scrolledToBottom =
        window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
    if (scrolledToBottom) return targets[targets.length - 1].id;

    let active = targets[0].id;
    for (const { id, el } of targets) {
        if (el.getBoundingClientRect().top - ACTIVE_OFFSET <= 0) active = id;
        else break;
    }
    return active;
};

const decodeHash = () => {
    try {
        return decodeURIComponent(window.location.hash.slice(1));
    } catch {
        return window.location.hash.slice(1);
    }
};

function RightNav({ contents: contentsProp }) {
    // Many pages build their contents array inside the component, so it's a new
    // array on every render. Keying on the text keeps the scroll listener (and
    // the URL-hash check) from re-running on unrelated re-renders.
    const contentsKey = contentsProp.join('\u0000');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const contents = useMemo(() => contentsProp, [contentsKey]);

    const [activeId, setActiveId] = useState(() => contents[0] ?? null);
    const [isOpen, setIsOpen] = useState(false);
    const lockUntilRef = useRef(0);
    const frameRef = useRef(0);

    // ---- Scroll spy -------------------------------------------------------
    useEffect(() => {
        if (!contents.length) return undefined;

        const update = () => {
            frameRef.current = 0;
            if (Date.now() < lockUntilRef.current) return;
            const next = findActiveId(contents);
            if (next) setActiveId((current) => (current === next ? current : next));
        };

        // One calculation per animation frame, however fast the scroll events fire.
        const onScroll = () => {
            if (!frameRef.current) frameRef.current = requestAnimationFrame(update);
        };

        // Capture phase so it also works if a page scrolls inside a container
        // rather than the window.
        document.addEventListener('scroll', onScroll, { passive: true, capture: true });
        window.addEventListener('resize', onScroll, { passive: true });

        // Arriving with #Section in the URL highlights that section; otherwise
        // work out the position straight away (e.g. after a refresh mid-page).
        const fromHash = decodeHash();
        if (fromHash && contents.includes(fromHash)) setActiveId(fromHash);
        else update();

        return () => {
            document.removeEventListener('scroll', onScroll, { capture: true });
            window.removeEventListener('resize', onScroll);
            if (frameRef.current) cancelAnimationFrame(frameRef.current);
            frameRef.current = 0;
        };
    }, [contents]);

    // ---- Click → smooth scroll -------------------------------------------
    const goTo = useCallback((e, id) => {
        e.preventDefault();
        setIsOpen(false);
        setActiveId(id);
        lockUntilRef.current = Date.now() + CLICK_LOCK_MS;

        // Let the page react first (e.g. expand a collapsed card), then scroll
        // once the layout has updated.
        window.dispatchEvent(new CustomEvent(DOCS_NAVIGATE_EVENT, { detail: id }));

        requestAnimationFrame(() =>
            requestAnimationFrame(() => {
                const el = document.getElementById(id);
                if (!el) return;
                // Keep headings clear of the sticky navbar on pages whose CSS
                // doesn't already set a scroll margin.
                if (!el.style.scrollMarginTop && getComputedStyle(el).scrollMarginTop === '0px') {
                    el.style.scrollMarginTop = `${ACTIVE_OFFSET - 24}px`;
                }
                el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                window.history.replaceState(null, '', `#${encodeURIComponent(id)}`);
            })
        );
    }, []);

    // Lock body scroll when the drawer is open on mobile
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    // Close the drawer on Escape
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape') setIsOpen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, []);

    const listMarkup = (
        <ul className="rn-list">
            <p className="rn-list-heading">CONTENTS</p>
            {contents.map((content) => {
                const isActive = activeId === content;
                return (
                    <li key={content} className="rn-item">
                        <a
                            href={`#${encodeURIComponent(content)}`}
                            onClick={(e) => goTo(e, content)}
                            className={`rn-link ${isActive ? 'rn-link-active' : ''}`}
                            aria-current={isActive ? 'location' : undefined}
                        >
                            {content}
                        </a>
                    </li>
                );
            })}
        </ul>
    );

    return (
        <>
            {/* DESKTOP: sticky rail */}
            <nav className="rn-desktop" aria-label="On this page">
                {listMarkup}
            </nav>

            {/* MOBILE: floating toggle button */}
            <button
                type="button"
                className="rn-toggle"
                aria-label="Open table of contents"
                aria-expanded={isOpen}
                onClick={() => setIsOpen(true)}
            >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="8" y1="6" x2="21" y2="6" />
                    <line x1="8" y1="12" x2="21" y2="12" />
                    <line x1="8" y1="18" x2="21" y2="18" />
                    <line x1="3" y1="6" x2="3.01" y2="6" />
                    <line x1="3" y1="12" x2="3.01" y2="12" />
                    <line x1="3" y1="18" x2="3.01" y2="18" />
                </svg>
                <span>On this page</span>
            </button>

            {/* MOBILE: backdrop */}
            <div
                className={`rn-backdrop ${isOpen ? 'rn-backdrop-open' : ''}`}
                onClick={() => setIsOpen(false)}
                aria-hidden="true"
            />

            {/* MOBILE: slide-in drawer */}
            <div
                className={`rn-drawer ${isOpen ? 'rn-drawer-open' : ''}`}
                role="dialog"
                aria-modal="true"
                aria-label="Table of contents"
            >
                <div className="rn-drawer-header">
                    <span className="rn-drawer-title">On this page</span>
                    <button
                        type="button"
                        className="rn-close"
                        aria-label="Close table of contents"
                        onClick={() => setIsOpen(false)}
                    >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                    </button>
                </div>
                <div className="rn-drawer-body">{listMarkup}</div>
            </div>
        </>
    );
}

export default RightNav;
